import React, { useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActionsUntyped from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import TextFieldFormik from '~/components/TextFieldFormik';

import ExtraHeadersEditor from './ExtraHeadersEditor';
import PayloadFieldsEditor from './PayloadFieldsEditor';
import QueryParamsEditor from './QueryParamsEditor';
import TEXTS from './texts';
import type { AvailableTool, ConfigFormValues, ExternalToolConfig } from './types';
import { configToFormValues } from './types';

const TOAST_ID = 'external-tool-config-errors';

// DialogFooterActions is a .jsx file; under allowJs, `primaryTooltip` is inferred as the literal
// `undefined` type because its JS default is `undefined`. Cast to the real shape it accepts.
interface DialogFooterActionsProps {
  disabled?: boolean;
  primaryDisabled?: boolean;
  primaryTooltip?: string;
  onClickPrimary?: () => void;
  onClickSecondary?: () => void;
}
const DialogFooterActions = DialogFooterActionsUntyped as React.ComponentType<DialogFooterActionsProps>;

interface ExternalToolConfigDialogProps {
  open: boolean;
  editedConfig: ExternalToolConfig | null;
  availableTools: AvailableTool[];
  existingToolNames: string[];
  isSaving: boolean;
  isToolWriting: boolean;
  onSubmit: (values: ConfigFormValues) => void;
  onClose: () => void;
}

const buildValidationSchema = () =>
  Yup.object().shape({
    tool_name: Yup.string().required(TEXTS.validation.toolRequired),
    url: Yup.string()
      .trim()
      .required(TEXTS.validation.urlRequired)
      .matches(/^https:\/\//, TEXTS.validation.urlHttps)
      // Beyond the https:// prefix, reject anything the browser can't parse as an absolute URL
      // (e.g. `https://`, `https://foo bar`) — the endpoint is called verbatim, so a malformed
      // value would only fail at request time. `new URL` is the same parser the runtime uses.
      .test('valid-url', TEXTS.validation.urlInvalid, (value) => {
        if (!value) return true;
        try {
          const parsed = new URL(value);
          return parsed.protocol === 'https:' && parsed.hostname !== '';
        } catch {
          return false;
        }
      }),
    payload_fields: Yup.array()
      .of(
        Yup.object()
          .shape({
            key: Yup.string().trim().required(TEXTS.validation.keyRequired),
            field_type: Yup.string(),
            title: Yup.string().trim().required(TEXTS.validation.titleRequired),
            description: Yup.string().trim().required(TEXTS.validation.descriptionRequired),
            enum_values: Yup.array().of(Yup.string()),
          })
          // enum_values only matters for enum fields, so validate it on the row itself. Done with a
          // `.test` rather than Yup's `.when` because the latter's signature differs across Yup majors.
          .test('enum-values-required', TEXTS.validation.enumRequired, (field) => {
            if (!field || field.field_type !== 'enum') return true;
            return (field.enum_values ?? []).some((value) => (value ?? '').trim() !== '');
          })
      )
      .test('unique-keys', TEXTS.validation.duplicateKey, (fields) => {
        const keys = (fields ?? []).map((field) => (field?.key ?? '').trim()).filter(Boolean);
        return new Set(keys).size === keys.length;
      }),
    query_params: Yup.array()
      .of(Yup.object().shape({ name: Yup.string(), value: Yup.string() }))
      // A row carrying a value must name it — otherwise the value is silently dropped on submit.
      .test('query-param-name-required', TEXTS.validation.queryParamNameRequired, (rows) =>
        (rows ?? []).every((row) => (row?.value ?? '').trim() === '' || (row?.name ?? '').trim() !== '')
      )
      // A named row must say which FiveSigma value fills it — an unbound param has no value to send
      // and the backend rejects it, so catch it here rather than round-tripping to find out.
      .test('query-param-value-required', TEXTS.validation.queryParamValueRequired, (rows) =>
        (rows ?? []).every((row) => (row?.name ?? '').trim() === '' || (row?.value ?? '').trim() !== '')
      )
      // Case-insensitive: param names are case-sensitive on the wire, but a name->source map keeps
      // only the last of two rows differing in case, so one would be silently dropped.
      .test('query-param-unique', TEXTS.validation.duplicateQueryParam, (rows) => {
        const names = (rows ?? []).map((row) => (row?.name ?? '').trim().toLowerCase()).filter(Boolean);
        return new Set(names).size === names.length;
      }),
    extra_headers: Yup.array()
      .of(Yup.object().shape({ name: Yup.string(), value: Yup.string() }))
      // Header rows only matter when the operator is replacing the set. Read the sibling mode from
      // the test context (`.test` rather than `.when` — the latter's signature differs across Yup majors).
      .test('extra-header-name-required', TEXTS.validation.headerNameRequired, (rows, ctx) => {
        if (ctx.parent?.extra_headers_mode !== 'replace') return true;
        // Every row carrying a value must name it — otherwise the value is silently dropped on submit.
        return (rows ?? []).every((row) => (row?.value ?? '').trim() === '' || (row?.name ?? '').trim() !== '');
      })
      .test('extra-header-unique', TEXTS.validation.duplicateHeader, (rows, ctx) => {
        if (ctx.parent?.extra_headers_mode !== 'replace') return true;
        const names = (rows ?? []).map((row) => (row?.name ?? '').trim()).filter(Boolean);
        return new Set(names).size === names.length;
      }),
  });

const DialogInner: React.FC<{
  isEdit: boolean;
  availableTools: AvailableTool[];
  existingToolNames: string[];
  liveIsEnabled: boolean | undefined;
  hasExtraHeaders: boolean | undefined;
}> = ({ isEdit, availableTools, existingToolNames, liveIsEnabled, hasExtraHeaders }) => {
  useFormikValidationErrorToast({ toastId: TOAST_ID, title: TEXTS.validation.toastTitle });
  const { setFieldValue, values } = useFormikContext<ConfigFormValues>();

  const selectedTool = availableTools.find((tool) => tool.tool_name === values.tool_name);
  // POST is the pre-selection default only; once a tool resolves, its declared method decides.
  const httpMethod = selectedTool?.http_method ?? 'POST';
  // Which FiveSigma-supplied values this tool's params can be bound to. Server-supplied per tool, so
  // a tool that carries its parameters in the body offers none and the editor is hidden entirely.
  const queryParamSources = selectedTool?.query_param_sources ?? [];
  // Only true once a tool has actually resolved: an unresolved selection (a stored config whose tool
  // the registry no longer lists) must leave the stored params alone rather than look like a tool
  // that supplies none, which the effect below would treat as a licence to clear them.
  const toolSuppliesNoValues = selectedTool !== undefined && queryParamSources.length === 0;

  // Keep only the enabled flag in sync with the live row (e.g. a failed row-toggle reverting
  // is_enabled underneath an open dialog), so a save never persists a stale enabled value. Scoped
  // to this one field — a blanket reinitialize would also wipe in-progress url/payload edits.
  useEffect(() => {
    if (liveIsEnabled !== undefined) setFieldValue('is_enabled', liveIsEnabled);
  }, [liveIsEnabled, setFieldValue]);
  // A GET has no request body, so drop any fields entered before the selection switched — they
  // would submit invisibly and the backend 400s them. Scoped to this field, like the sync above.
  useEffect(() => {
    if (httpMethod === 'GET') setFieldValue('payload_fields', []);
  }, [httpMethod, setFieldValue]);
  // Symmetrically: a tool that supplies no values can have no bindable params, so the editor is
  // hidden and any rows entered against the previously selected tool must go with it — otherwise
  // they submit invisibly and the backend 400s a param the operator can no longer see.
  useEffect(() => {
    if (toolSuppliesNoValues) setFieldValue('query_params', []);
  }, [toolSuppliesNoValues, setFieldValue]);
  // On create, hide tools already configured for this org (a duplicate (org, tool) is a 400).
  const selectableTools = isEdit
    ? availableTools
    : availableTools.filter((tool) => !existingToolNames.includes(tool.tool_name));

  return (
    <Form className="flex flex-col gap-16">
      <ValidationErrorToastContainer containerId={TOAST_ID} />
      <TextFieldFormik select id="tool_name" name="tool_name" label={TEXTS.fields.tool} disabled={isEdit} fullWidth>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore TextFieldFormik (JS) is not typed for select children */}
        {selectableTools.map((tool) => (
          <MenuItem key={tool.tool_name} value={tool.tool_name}>
            {tool.tool_title}
          </MenuItem>
        ))}
      </TextFieldFormik>
      <TextFieldFormik id="url" name="url" label={TEXTS.fields.url} fullWidth />
      <Text variant={Text.VARIANTS.XS}>{TEXTS.fields.methodHelper(httpMethod)}</Text>
      {queryParamSources.length > 0 && <QueryParamsEditor sources={queryParamSources} />}
      {httpMethod === 'POST' && <PayloadFieldsEditor />}
      <ExtraHeadersEditor isEdit={isEdit} hasExtraHeaders={hasExtraHeaders} />
      <CheckboxFormik id="is_enabled" label={TEXTS.fields.enabled} />
    </Form>
  );
};

const ExternalToolConfigDialog: React.FC<ExternalToolConfigDialogProps> = ({
  open,
  editedConfig,
  availableTools,
  existingToolNames,
  isSaving,
  isToolWriting,
  onSubmit,
  onClose,
}) => {
  if (!open) return null;
  const isEdit = editedConfig !== null;

  return (
    <Formik
      // Formik reads initialValues only on mount, so switching tool/mode while the dialog stays
      // open (e.g. clicking Add during an edit) would otherwise keep the previous edit's values and
      // POST them. Key on the edited tool so Formik remounts with fresh values on any identity change,
      // while edits within the same edit session are preserved.
      key={editedConfig?.tool_name ?? '__create__'}
      initialValues={configToFormValues(editedConfig)}
      validationSchema={buildValidationSchema()}
      onSubmit={(values) => {
        // Save is disabled while a save is in flight or a row toggle PUT is running for this tool,
        // but Formik's Form still submits on Enter. Mirror the disabled state here so Enter can't
        // bypass it — otherwise a blocked submit silently no-ops and looks like it saved.
        if (isSaving || isToolWriting) return;
        onSubmit(values);
      }}
    >
      {({ submitForm }) => (
        <CardDialog
          isDialog
          open
          fullWidth
          maxWidth="md"
          title={isEdit ? TEXTS.editConfig : TEXTS.createConfig}
          onClose={onClose}
          preventClose={isSaving}
          footerActions={
            <DialogFooterActions
              onClickPrimary={submitForm}
              onClickSecondary={onClose}
              disabled={isSaving}
              primaryDisabled={isToolWriting}
              primaryTooltip={isToolWriting ? TEXTS.saveBlockedByToggle : undefined}
            />
          }
        >
          <DialogInner
            isEdit={isEdit}
            availableTools={availableTools}
            existingToolNames={existingToolNames}
            liveIsEnabled={editedConfig?.is_enabled}
            hasExtraHeaders={editedConfig?.has_extra_headers}
          />
        </CardDialog>
      )}
    </Formik>
  );
};

export default ExternalToolConfigDialog;
